/**
 * Owner: Masa Bayu
 * File: admin-dashboard.ts
 * Date:Time: 2026-10-23 09:15
 * Description: §467-D — klien dashboard PENTADBIR qxread. Sumber data HANYA
 *   `GET /v1/admin/dashboard` qxp-hub dengan Bearer sesi hub (§467-B/§467d-R).
 *   Pola dikekalkan sama seperti `publisher-dashboard.ts` §467-C: penghuraian
 *   GAGAL-KERAS (respons tak boleh dihurai = `error`, bukan dashboard kosong),
 *   tiada nilai lalai rekaan, dan TIADA laluan tulis — pentadbir memantau, ia
 *   tidak menetapkan harga (harga hanya masuk semasa Publish di hub).
 *
 *   Status: `anonymous` (tiada token) · `loading` · `ready` · `forbidden`
 *   (sesi sah tapi bukan pentadbir — 403) · `error`.
 */

import { useSyncExternalStore } from 'react';
import { clearHubAuth, getHubAuthToken } from '../publication/hub-auth';
import { expireHubSession, qxpHubOrigin } from '../publication/hub-sync';

export interface HubMoney {
  readonly currency: string;
  readonly minorUnits: number;
  readonly orders: number;
}

export interface HubMonthPoint {
  readonly month: string;
  readonly orders: number;
  readonly revenueByCurrency: readonly HubMoney[];
}

export interface HubOrderRow {
  readonly orderId: string;
  readonly publicationId: string;
  readonly title: string;
  readonly amountMinorUnits: number | null;
  readonly currency: string | null;
  readonly state: string | null;
  readonly createdAt: string | null;
  readonly paidAt: string | null;
  readonly buyerEmail: string | null;
}

export interface HubAdminBook {
  readonly publicationId: string;
  readonly title: string;
  readonly languageRef: string | null;
  readonly revision: number | null;
  readonly publishedAt: string | null;
  readonly priceMinorUnits: number | null;
  readonly currency: string | null;
  readonly category: string | null;
  readonly subcategory: string | null;
  readonly artifactBaseUrl: string | null;
  readonly ownerAccountId: string | null;
  readonly ownerEmail: string | null;
  readonly artifactPresent: boolean;
  readonly sales: {
    readonly orders: number;
    readonly buyers: number;
    readonly revenueByCurrency: readonly HubMoney[];
  };
}

export interface HubAdminAccount {
  readonly accountId: string;
  readonly email: string | null;
  readonly role: string;
  readonly displayName: string;
  readonly publishedCount: number;
  readonly activeGrants: number;
  readonly orders: number;
  readonly createdAt: string | null;
  readonly hasPassword: boolean;
  readonly googleLinked: boolean;
}

export interface AdminDashboardView {
  readonly generatedAt: string;
  /** 'token' untuk pemegang QXP_HUB_TOKEN; selain itu accountId sesi. */
  readonly actor: string;
  readonly catalog: {
    readonly version: number | string | null;
    readonly updatedAt: string | null;
    readonly publications: number;
    readonly pricedPublications: number;
    readonly unownedPublications: readonly string[];
  };
  readonly counts: {
    readonly accounts: {
      readonly total: number;
      readonly reader: number;
      readonly publisher: number;
      readonly admin: number;
    };
    readonly orders: { readonly total: number; readonly byState: Readonly<Record<string, number>> };
    readonly revenueByCurrency: readonly HubMoney[];
    readonly entitlementFiles: number;
    readonly activeGrants: number;
    readonly sessions: { readonly total: number; readonly active: number };
    readonly artifactDirs: number;
    readonly takedownDirs: number;
  };
  readonly publications: readonly HubAdminBook[];
  readonly accounts: readonly HubAdminAccount[];
  readonly monthly: readonly HubMonthPoint[];
  readonly recentOrders: readonly HubOrderRow[];
  readonly artifactRefs: readonly string[];
  readonly takedownRefs: readonly string[];
}

export type AdminDashboardStatus = 'anonymous' | 'loading' | 'ready' | 'forbidden' | 'error';

export interface AdminDashboardState {
  readonly status: AdminDashboardStatus;
  readonly view: AdminDashboardView | null;
  readonly error: string | null;
  readonly fetchedAt: string | null;
}

const IDLE: AdminDashboardState = { status: 'anonymous', view: null, error: null, fetchedAt: null };

let state: AdminDashboardState = IDLE;
let inflight: Promise<AdminDashboardState> | null = null;
const listeners = new Set<() => void>();

function emit(next: AdminDashboardState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function getAdminDashboard(): AdminDashboardState {
  return state;
}

export function subscribeAdminDashboard(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAdminDashboard(): AdminDashboardState {
  return useSyncExternalStore(subscribeAdminDashboard, getAdminDashboard, getAdminDashboard);
}

// ─── penghuraian (gagal-keras) ───────────────────────────────────────────────

type Record_ = Record<string, unknown>;

function isRecord(value: unknown): value is Record_ {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function strOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function intOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

function int(value: unknown): number {
  return intOrNull(value) ?? 0;
}

function bool(value: unknown): boolean {
  return value === true;
}

export function parseMoneyList(value: unknown): readonly HubMoney[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isRecord)
    .map((row) => ({
      currency: str(row.currency),
      minorUnits: int(row.minorUnits),
      orders: int(row.orders),
    }))
    .filter((row) => row.currency.length > 0);
}

/** Satu baris order — bentuk sama dengan `recentOrders` dashboard (SSOT hub). */
export function parseOrderRow(value: unknown): HubOrderRow | null {
  if (!isRecord(value)) return null;
  const orderId = str(value.orderId);
  if (orderId.length === 0) return null;
  return {
    orderId,
    publicationId: str(value.publicationId),
    title: str(value.title) || str(value.publicationId),
    amountMinorUnits: intOrNull(value.amountMinorUnits),
    currency: strOrNull(value.currency),
    state: strOrNull(value.state),
    createdAt: strOrNull(value.createdAt),
    paidAt: strOrNull(value.paidAt),
    buyerEmail: strOrNull(value.buyerEmail),
  };
}

export function parseOrderRows(value: unknown): readonly HubOrderRow[] | null {
  if (!Array.isArray(value)) return null;
  return value.map(parseOrderRow).filter((o): o is HubOrderRow => o !== null);
}

function parseMonthly(value: unknown): readonly HubMonthPoint[] | null {
  if (!Array.isArray(value)) return null;
  return value
    .filter(isRecord)
    .map((m) => ({
      month: str(m.month),
      orders: int(m.orders),
      revenueByCurrency: parseMoneyList(m.revenueByCurrency),
    }))
    .filter((m) => m.month.length > 0);
}

function parseBook(value: unknown): HubAdminBook | null {
  if (!isRecord(value)) return null;
  const publicationId = str(value.publicationId);
  if (publicationId.length === 0) return null;
  const sales = isRecord(value.sales) ? value.sales : {};
  return {
    publicationId,
    title: str(value.title) || publicationId,
    languageRef: strOrNull(value.languageRef),
    revision: intOrNull(value.revision),
    publishedAt: strOrNull(value.publishedAt),
    priceMinorUnits: intOrNull(value.priceMinorUnits),
    currency: strOrNull(value.currency),
    category: strOrNull(value.category),
    subcategory: strOrNull(value.subcategory),
    artifactBaseUrl: strOrNull(value.artifactBaseUrl),
    ownerAccountId: strOrNull(value.ownerAccountId),
    ownerEmail: strOrNull(value.ownerEmail),
    artifactPresent: bool(value.artifactPresent),
    sales: {
      orders: int(sales.orders),
      buyers: int(sales.buyers),
      revenueByCurrency: parseMoneyList(sales.revenueByCurrency),
    },
  };
}

function parseAccount(value: unknown): HubAdminAccount | null {
  if (!isRecord(value)) return null;
  const accountId = str(value.accountId);
  if (accountId.length === 0) return null;
  return {
    accountId,
    email: strOrNull(value.email),
    role: str(value.role),
    displayName: str(value.displayName),
    publishedCount: int(value.publishedCount),
    activeGrants: int(value.activeGrants),
    orders: int(value.orders),
    createdAt: strOrNull(value.createdAt),
    hasPassword: bool(value.hasPassword),
    googleLinked: bool(value.googleLinked),
  };
}

function stringList(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Kiraan keadaan daripada `counts.orders` hub. §467-B menyalin kiraan keadaan
 * SECARA RATA ke dalam objek yang sama dengan `total`
 * (`{total:7, paid:5, pending:2}`), jadi `total` — yang dibaca berasingan
 * sebagai `orders.total` — mesti tidak dianggap satu keadaan; jika tidak, UI
 * memaparkan pill "total · 7" seperti ia satu keadaan order.
 */
function stateCounts(value: unknown): Readonly<Record<string, number>> {
  if (!isRecord(value)) return {};
  const out: Record<string, number> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (key === 'total') continue;
    if (typeof raw === 'number' && Number.isInteger(raw)) out[key] = raw;
  }
  return out;
}

/** null = bentuk respons bukan dashboard admin; pemanggil paparkan ralat, bukan 0. */
export function parseAdminDashboard(body: unknown): AdminDashboardView | null {
  if (!isRecord(body) || body.ok !== true) return null;
  const catalog = isRecord(body.catalog) ? body.catalog : null;
  const counts = isRecord(body.counts) ? body.counts : null;
  if (catalog === null || counts === null) return null;

  const publications = Array.isArray(body.publications)
    ? body.publications.map(parseBook).filter((p): p is HubAdminBook => p !== null)
    : null;
  const accounts = Array.isArray(body.accounts)
    ? body.accounts.map(parseAccount).filter((a): a is HubAdminAccount => a !== null)
    : null;
  const monthly = parseMonthly(body.monthly);
  const recentOrders = parseOrderRows(body.recentOrders);
  if (publications === null || accounts === null || monthly === null || recentOrders === null) {
    return null;
  }

  const accountCounts = isRecord(counts.accounts) ? counts.accounts : {};
  const orderCounts = isRecord(counts.orders) ? counts.orders : {};
  const byState = stateCounts(orderCounts);
  const sessions = isRecord(counts.sessions) ? counts.sessions : {};

  return {
    generatedAt: str(body.generatedAt),
    actor: str(body.actor),
    catalog: {
      version: typeof catalog.version === 'number' ? catalog.version : strOrNull(catalog.version),
      updatedAt: strOrNull(catalog.updatedAt),
      publications: int(catalog.publications),
      pricedPublications: int(catalog.pricedPublications),
      unownedPublications: stringList(catalog.unownedPublications),
    },
    counts: {
      accounts: {
        total: int(accountCounts.total),
        reader: int(accountCounts.reader),
        publisher: int(accountCounts.publisher),
        admin: int(accountCounts.admin),
      },
      orders: { total: int(orderCounts.total), byState },
      revenueByCurrency: parseMoneyList(counts.revenueByCurrency),
      entitlementFiles: int(counts.entitlementFiles),
      activeGrants: int(counts.activeGrants),
      sessions: { total: int(sessions.total), active: int(sessions.active) },
      artifactDirs: int(counts.artifactDirs),
      takedownDirs: int(counts.takedownDirs),
    },
    publications,
    accounts,
    monthly,
    recentOrders,
    artifactRefs: stringList(body.artifactRefs),
    takedownRefs: stringList(body.takedownRefs),
  };
}

// ─── rantaian ────────────────────────────────────────────────────────────────

export type HubFetchInit = { readonly method?: string; readonly headers?: Record<string, string> };
export type HubFetch = (url: string, init?: HubFetchInit) => Promise<Response>;

/** Mesej ralat hub = `{ok:false, error:'…'}`; selain itu mesej generik berjujur. */
export async function hubErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as Record_;
    const message = str(body.error);
    if (message.length > 0) return message;
  } catch {
    /* badan bukan JSON */
  }
  return `Pelayan memulangkan HTTP ${String(res.status)}`;
}

function fail(error: string): AdminDashboardState {
  emit({ ...state, status: 'error', error });
  return state;
}

/**
 * Tarik dashboard admin. `force=false` (lalai) memakai cached `ready` untuk
 * render pertama; token tiada → `anonymous`.
 */
export async function refreshAdminDashboard(
  options: {
    readonly force?: boolean;
    readonly fetchImpl?: HubFetch;
    readonly origin?: string;
  } = {},
): Promise<AdminDashboardState> {
  const token = getHubAuthToken();
  if (token === null) {
    emit(IDLE);
    return IDLE;
  }
  if (state.status === 'ready' && options.force !== true) return state;
  if (inflight !== null) return inflight;

  emit({ ...state, status: 'loading' });
  const fetchImpl = options.fetchImpl ?? fetch;
  const origin = options.origin ?? qxpHubOrigin();

  const task = (async (): Promise<AdminDashboardState> => {
    let res: Response;
    try {
      res = await fetchImpl(`${origin}/v1/admin/dashboard`, {
        method: 'GET',
        headers: { authorization: `Bearer ${token}` },
      });
    } catch {
      return fail('Pelayan identiti tidak dapat dicapai');
    } finally {
      inflight = null;
    }
    if (res.status === 401) {
      expireHubSession();
      emit(IDLE);
      return IDLE;
    }
    if (res.status === 403) {
      emit({ status: 'forbidden', view: null, error: await hubErrorMessage(res), fetchedAt: null });
      return state;
    }
    if (!res.ok) return fail(await hubErrorMessage(res));
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      return fail('Respons dashboard bukan JSON');
    }
    const view = parseAdminDashboard(body);
    if (view === null) return fail('Respons dashboard tidak dapat dihurai');
    emit({ status: 'ready', view, error: null, fetchedAt: view.generatedAt });
    return state;
  })();

  inflight = task;
  return task;
}

/** Buang cache setempat (log keluar / tukar akaun). */
export function resetAdminDashboard(): void {
  inflight = null;
  emit(IDLE);
}

/** Pembantu ujian: log keluar penuh + stor admin kosong. */
export function __resetAdminDashboardForTests(): void {
  clearHubAuth();
  resetAdminDashboard();
}
